import React from "react";
import { CrudModal } from "./crud";

const options = (values) => values.map((value) => ({ label: value, value }));

export function PersonForm({ open, onClose, onSubmit, initialValues, companies = [], submitLabel }) {
  const companyOptions = [{ label: "No company", value: "" }, ...companies.map((company) => ({ label: company.name, value: company.id }))];
  const fields = [
    { key: "name", label: "Name", required: true, autoFocus: true },
    { key: "email", label: "Email", type: "email" },
    { key: "company_id", label: "Company", type: "select", options: companyOptions },
    { key: "job_title", label: "Job title" },
    { key: "location", label: "Location" },
    { key: "phone", label: "Phone" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "timezone", label: "Timezone", type: "select", options: options(["America/New_York", "America/Los_Angeles", "Europe/London", "Europe/Berlin", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo"]) },
    { key: "connection_strength", label: "Connection strength", type: "select", options: options(["Very strong", "Strong", "Medium", "Weak"]) },
    { key: "notes", label: "Notes", type: "textarea", fullWidth: true },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit person" : "New person"} description="Keep the record details current for outreach and relationship tracking." fields={fields} initialValues={{ timezone: "America/New_York", connection_strength: "Weak", ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Create person")} size="max-w-2xl" />;
}

export function CompanyForm({ open, onClose, onSubmit, initialValues, submitLabel }) {
  const fields = [
    { key: "name", label: "Company name", required: true, autoFocus: true },
    { key: "domain", label: "Domain", placeholder: "example.com" },
    { key: "industry", label: "Industry" },
    { key: "employees", label: "Employees" },
    { key: "location", label: "Location" },
    { key: "arr", label: "ARR" },
    { key: "status", label: "Status", type: "select", options: options(["Lead", "Prospect", "Customer"]) },
    { key: "description", label: "Description", type: "textarea", rows: 5, fullWidth: true },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit company" : "New company"} fields={fields} initialValues={{ status: "Lead", ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Create company")} />;
}

export function DealForm({ open, onClose, onSubmit, initialValues, companies = [], submitLabel }) {
  const companyOptions = [{ label: "No company", value: "" }, ...companies.map((company) => ({ label: company.name, value: company.id }))];
  const fields = [
    { key: "name", label: "Deal name", required: true, autoFocus: true },
    { key: "company_id", label: "Company", type: "select", options: companyOptions },
    { key: "stage", label: "Stage", type: "select", options: options(["Discovery", "Qualified", "Proposal", "Negotiation", "Closed Won"]) },
    { key: "value", label: "Value", placeholder: "$0" },
    { key: "owner", label: "Owner" },
    { key: "close_date", label: "Close date", type: "date" },
    { key: "probability", label: "Probability", type: "number", min: 0, max: 100, step: 1 },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit deal" : "New deal"} fields={fields} initialValues={{ stage: "Discovery", value: "$0", owner: "Alex Rivera", probability: 20, ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Create deal")} />;
}

export function SequenceForm({ open, onClose, onSubmit, initialValues, submitLabel }) {
  const fields = [
    { key: "name", label: "Name", required: true, autoFocus: true },
    { key: "goal", label: "Goal" },
    { key: "num_touches", label: "Target touches", type: "number", min: 1, max: 7, step: 1 },
    { key: "tone", label: "Tone" },
    { key: "guidance", label: "Guidance for Hermes", type: "textarea", fullWidth: true },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit sequence" : "New sequence"} fields={fields} initialValues={{ goal: "Book a meeting", tone: "Friendly, concise, consultative", num_touches: 3, ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Create sequence")} size="max-w-xl" />;
}

export function TaskForm({ open, onClose, onSubmit, initialValues, submitLabel }) {
  const fields = [
    { key: "title", label: "Task", required: true, autoFocus: true, fullWidth: true },
    { key: "type", label: "Type", type: "select", options: options(["general", "sequence", "research", "content", "enrichment"]) },
    { key: "status", label: "Status", type: "select", options: options(["queued", "in_progress", "done"]) },
    { key: "related_to", label: "Related to" },
    { key: "agent", label: "Agent" },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit task" : "New task"} fields={fields} initialValues={{ type: "general", status: "queued", agent: "Hermes", ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Create task")} />;
}

export function ContentForm({ open, onClose, onSubmit, initialValues, submitLabel }) {
  const fields = [
    { key: "channel", label: "Channel", type: "select", options: options(["twitter", "linkedin", "instagram"]) },
    { key: "scheduled_date", label: "Scheduled date", type: "date" },
    { key: "scheduled_time", label: "Scheduled time", type: "time" },
    { key: "status", label: "Status", type: "select", options: options(["draft", "pending_approval", "approved", "published"]) },
    { key: "content", label: "Content", type: "textarea", rows: 6, required: true, fullWidth: true, autoFocus: true },
    { key: "research", label: "Research notes", type: "textarea", rows: 3, fullWidth: true },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit post" : "New post"} fields={fields} initialValues={{ channel: "twitter", status: "draft", ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Create post")} size="max-w-2xl" />;
}

export function SocialProfileForm({ open, onClose, onSubmit, initialValues, submitLabel }) {
  const fields = [
    { key: "handle", label: "Handle", required: true, autoFocus: true, placeholder: "@handle" },
    { key: "name", label: "Name" },
    { key: "platform", label: "Platform", type: "select", options: options(["twitter", "linkedin", "instagram"]) },
    { key: "followers", label: "Followers" },
    { key: "bio", label: "Bio", type: "textarea", fullWidth: true },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit social profile" : "Follow a social profile"} fields={fields} initialValues={{ platform: "twitter", ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Add profile")} />;
}

export function SocialPostForm({ open, onClose, onSubmit, initialValues, submitLabel }) {
  const fields = [
    { key: "profile_handle", label: "Profile handle", required: true, autoFocus: true },
    { key: "profile_name", label: "Profile name" },
    { key: "platform", label: "Platform", type: "select", options: options(["twitter", "linkedin", "instagram"]) },
    { key: "posted_at", label: "Posted at" },
    { key: "url", label: "Post URL", type: "url" },
    { key: "content", label: "Post content", type: "textarea", rows: 4, required: true, fullWidth: true },
    { key: "our_response", label: "Our response", type: "textarea", rows: 4, fullWidth: true },
    { key: "response_status", label: "Response status", type: "select", options: options(["none", "draft", "approved", "posted"]) },
  ];
  return <CrudModal open={open} onClose={onClose} title={initialValues?.id ? "Edit tracked post" : "Track a social post"} fields={fields} initialValues={{ platform: "twitter", response_status: "none", ...initialValues }} onSubmit={onSubmit} submitLabel={submitLabel || (initialValues?.id ? "Save changes" : "Add post")} size="max-w-2xl" />;
}
